import nsdcLogo from '@assets/branding/nsdc-trust.svg';
import trentialLogo from '@assets/branding/trential-with-text.svg';
import lockArt from '@assets/form//lock-art.svg';
import StandardButton from '@components/ui/buttons/standard-button';
import { WES_ORG } from '@global/constants/general';
import { TxnState } from '@global/constants/verification';
import { useIsDesktop } from '@global/hooks';
import { Box, Button, Grid, Skeleton, Typography } from '@mui/material';
import { updateFormData } from '@redux/slices/form-data';
import { RootState } from '@redux/store';
import { navigateTo } from '@services/navigation';
import { Dispatch, SetStateAction } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface BeginFormVerificationProps {
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  orgIdBasedAccessAllowed: boolean;
}

function BeginFormVerification(props: BeginFormVerificationProps) {
  const isDesktop = useIsDesktop();
  const { isLoading, setIsLoading, orgIdBasedAccessAllowed } = props;
  const dispatch = useDispatch();
  const formData = useSelector((s: RootState) => s.formData);
  const auth: any = useSelector((s: RootState) => s.auth);
  const { accessToken } = auth;
  const { orgInfo, txnInfo } = formData ?? {};
  const wesOrg = import.meta.env.VITE_ORG === WES_ORG;

  const handleProceed = () => {
    if (!accessToken) {
      navigateTo('/login');
      return;
    }
    dispatch(
      updateFormData({
        showInitScreen: false
      })
    );
  };

  const isExpired = () => {
    return (
      formData?.txnInfo?.form?.isExpired && formData?.txnInfo?.state === TxnState.NOT_INITIALIZED
    );
  };

  const isFormExpired = isExpired();

  const getBtnText = () => {
    if (isFormExpired) {
      return 'Form Expired';
    }
    if (txnInfo && txnInfo?.state !== TxnState.NOT_INITIALIZED) {
      return 'View Verification';
    } else {
      return orgIdBasedAccessAllowed ? 'Start Verification' : 'Access not allowed';
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        height: '100%',
        overflowY: 'auto',
        m: !accessToken && isDesktop ? '0 20vw' : 0
      }}>
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexFlow: 'column',
          justifyContent: 'space-between'
        }}>
        <Box
          sx={{
            display: 'flex',
            flexFlow: 'column',
            rowGap: '3.8rem',
            mt: { xs: '9.5vh', sm: '12vh' }
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: '3.5rem' }}>
            <img
              src={orgInfo?.imageUrl}
              onLoad={() => setIsLoading(false)}
              alt="org-image"
              style={{
                backgroundColor: '#f7f7f7',
                height: isLoading ? 0 : '7.2rem',
                width: isLoading ? 0 : '7.2rem',
                border: '1px solid lightgray',
                borderRadius: '50%'
              }}
            />
            {isLoading ? (
              <Skeleton
                variant="rounded"
                style={{ height: '3rem', width: '50vw', marginLeft: '1.7em' }}
              />
            ) : (
              <Typography variant="h6" sx={{ fontFamily: 'Poppins', ml: '1.7rem' }}>
                {orgInfo?.name}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              ml: '3.5rem',
              alignItems: 'center'
            }}>
            <Typography variant="h1" sx={{ fontFamily: 'Poppins', color: 'font.disabled' }}>
              All-in-one
              <br />
              <Box component="span" sx={{ color: 'font.dark' }}>
                {' '}
                certificate verification{' '}
              </Box>
              <br />
              platform
            </Typography>
            <img src={lockArt} alt="lock" style={{ height: '18.5rem', width: 'auto' }} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexFlow: 'column',
              alignItems: 'center',
              m: '0 3rem',
              mt: '3rem',
              rowGap: '.4rem'
            }}>
            <StandardButton sx={{ width: '100%' }} onClick={handleProceed} disabled={isFormExpired}>
              {getBtnText()}
            </StandardButton>
            {(!orgIdBasedAccessAllowed || isFormExpired) && (
              <Button sx={{ width: 'fit-content' }} onClick={() => navigateTo('/')}>
                Go Back to Home
              </Button>
            )}
          </Box>
        </Box>
        {wesOrg && (
          <Grid
            container
            sx={{
              margin: 'auto',
              width: '100%',
              columnGap: '5rem',
              rowGap: '5rem',
              flexDirection: !isDesktop ? 'column' : 'row'
            }}
            justifyContent={'center'}
            alignItems="center">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                columnGap: '.5rem',
                mt: '1.5rem'
              }}>
              <Typography variant="body1">Partnered with</Typography>
              <Box
                component="img"
                src={nsdcLogo}
                alt="nsdc"
                sx={{ height: { xs: '4.1rem', sm: '5rem' }, width: 'auto' }}
              />
            </Box>
          </Grid>
        )}
        {!isDesktop && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              columnGap: '.5rem',
              padding: '2rem 0',
              pt: '2.5rem'
            }}>
            <Typography variant="body1">Powered by</Typography>
            <img src={trentialLogo} alt="trential" style={{ height: '2rem', width: 'auto' }} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default BeginFormVerification;
